import type {
  NodeAccomodationFragment,
  NodeEditorialFragment,
  NodePlaceFragment,
  NodeSinglePageApplicationFragment,
  NodeTourFragment,
} from '#graphql-operations'
import type { Head } from '@unhead/vue'

export function usePageTracking(
  entity: ComputedRef<
    | NodeEditorialFragment
    | NodeAccomodationFragment
    | NodePlaceFragment
    | NodeTourFragment
    | NodeSinglePageApplicationFragment
  >,
  metatags: ComputedRef<Head>,
) {
  // It can be possible that we actually don't get anything here, due to the way
  // that redirects work in Nuxt: It's throwing an error, however page rendering
  // can continue in some cases.
  if (!entity || !entity.value || !metatags || !metatags.value) {
    return
  }

  const { trackSwitchPage } = useAnalytics()
  const route = useRoute()
  const language = useCurrentLanguage()
  const url = useRequestURL()
  // Provided by the analytics plugin.
  const previousPath = useState('previousPath', () => '')
  const ssrTrackedPath = useState('ssrTrackedPath', () => '')

  function getRouteName() {
    const name = route.name?.toString()
    if (!name) {
      return ''
    }

    // The route name contains a language suffix, e.g. home___de. We are only
    // interested in the party without the suffix.
    const parts = name.split('___')
    return parts[0] as string
  }

  function getPageTitle() {
    // Use the provided title if available.
    if (metatags.value.title) {
      return metatags.value.title.toString()
    }

    // On the client we can rely on this being updated at that point.
    if (import.meta.client) {
      return document.title
    }

    return ''
  }

  function getReferrer(): string {
    if (import.meta.server) {
      return 'MAGIC_REFERRER_STRING'
    }

    // No referrer if the path is the same one.
    if (document.location.pathname === previousPath.value) {
      return ''
    }

    return document.location.origin + previousPath.value
  }

  function getPageLanguage(): string {
    const pathLanguage = route.path.split('/')[1]
    if (pathLanguage && ['de', 'fr', 'it', 'en', 'es'].includes(pathLanguage)) {
      return pathLanguage
    }

    return language.value
  }

  // This is the case during client side hydration: The event has already been
  // pushed to the datalayer in this case, so we don't want to push it twice.
  if (import.meta.client && ssrTrackedPath.value === route.fullPath) {
    // Reset the value to an empty string, so that when the user visits the same
    // page again we actually to track it.
    ssrTrackedPath.value = ''
    return
  }

  if (import.meta.server) {
    ssrTrackedPath.value = route.fullPath
  }

  trackSwitchPage(
    {
      pageURL: url.href,
      pagePath: route.fullPath,
      pageTitle: getPageTitle(),
      pageReferrer: getReferrer(),
      pageLanguage: getPageLanguage(),
      logoThemeId: entity.value.logoTheme?.id,
      routeName: getRouteName(),
      nodeId: entity.value.nid,
      contextChannelName: entity.value.channel,
    },
    import.meta.server,
  )
}
